/* Home dashboard — one compact "next upcoming dates" widget per assignment category.
 * Each pulls the same schedule data the full scheduling pages use, shows the next ~3
 * dated entries with who's assigned, and links to that category's page. Territories have
 * no forward schedule, so that widget surfaces the soonest territories DUE BACK instead. */
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import QueryKeys from "../../api/queryKeys";
import { midweekApi } from "../../api/midweek";
import { useWMSchedules } from "../../api/weekend";
import { useOtherSchedule } from "../../api/otherSchedule";
import { useAVAssignments } from "../../api/avAttendant";
import { useCustomAssignments } from "../../api/customAssignments";
import { useTerritoryRecords } from "../../api/territory";
import { useCongSettings } from "../../api/cong";
import { useEvents } from "../../api/meetings";
import useFSGroups from "../../api/fsgroup";
import { useUsers } from "../../api/user";
import { userIdMap, nameOfUser } from "../../helpers/user";
import { Month, addDaysToISODate, dateStringCompare, getDayjs, mmDate, wmDate } from "../../helpers/dateHelpers";
import { getStoredLangGroupId, selectedCong } from "../../helpers/langGroups";
import { HGContext } from "../../helpers/globals";
import { wmScheduleIsBlank } from "../../types/scheduling/weekend";
import { OtherScheduleKind, OtherSchedule } from "../../types/scheduling/other";
import { AVAttendantAssignment } from "../../types/scheduling/avAttendant";
import { returnByDate } from "../congregation/territory/return_calc";
import { useTerritoryMap, terrLabel } from "../congregation/territory/common";
import { cleaningAssigneeName } from "../scheduling/other/common";
import { noMeetingEvents } from "../scheduling/wm/wm_common";
import { PreviewRow, SchedulePreview, chipsFrom, eventLabel, noMidweekMeetingEvents, useCongId, useShortDate } from "./homeShared";
import { tPositional } from "../../helpers/i18n";

const todayISO = () => new Date().toISOString().slice(0, 10);

/** Next few midweek meetings — date + chairman. */
export function MidweekUpcomingWidget() {
  const { t } = useTranslation();
  const ctx = useContext(HGContext);
  const cid = useCongId();
  const cong = ctx.globals.cong;
  const from = todayISO();
  const to = addDaysToISODate(from, 42);
  const userMap = userIdMap(useUsers().data ?? []);
  const mm = useQuery({
    queryKey: [QueryKeys.MidweekInfo, cid, from, to],
    queryFn: () => midweekApi.getMeetingsInfo(from, to, cid, false),
    enabled: !!cid,
  }).data;
  const rows: PreviewRow[] = (mm?.schedules ?? [])
    .filter((s) => s.date >= from)
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .slice(0, 3)
    .map((s) => {
      const cancelled = !!s.event && noMidweekMeetingEvents.has(s.event.event);
      return {
        key: s.id,
        dateISO: cong ? mmDate(s.date, cong, s.event ? [s.event] : undefined) : s.date,
        primary: cancelled ? eventLabel(t, s.event!) : s.chairman ? (userMap.get(s.chairman)?.displayName ?? "—") : "—",
        secondary: cancelled ? "" : t("schedules.chairman"),
      };
    });
  return <SchedulePreview rows={rows} to="/scheduling/mm" />;
}

/** Next few weekend meetings — public-talk title + speaker. */
export function WeekendUpcomingWidget() {
  const { t } = useTranslation();
  const ctx = useContext(HGContext);
  const cid = useCongId();
  const cong = ctx.globals.cong;
  const from = todayISO();
  const to = addDaysToISODate(from, 84);
  const schedules = useWMSchedules(from, to, cid, { enabled: !!cid }).data ?? [];
  const rows: PreviewRow[] = schedules
    .filter((w) => !wmScheduleIsBlank(w) && w.date >= from)
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .slice(0, 3)
    .map((w) => {
      const cancelled = !!w.event && noMeetingEvents.has(w.event.event);
      return {
        key: w.date,
        dateISO: cong ? wmDate(w.date, cong, w.event ? [w.event] : undefined) : w.date,
        // schedules.assignment.public-talk is "Public Talk {{0}}" — no talk number here, so blank it and trim.
        primary: cancelled
          ? eventLabel(t, w.event!)
          : w.public_talk?.title || tPositional(t, "schedules.assignment.public-talk", "").trim(),
        secondary: cancelled ? "" : w.speaker ? nameOfUser(w.speaker) : "",
      };
    });
  return <SchedulePreview rows={rows} to="/scheduling/wm" />;
}

/** Next few cleaning dates — the assigned publisher(s) or group per date. */
export function CleaningWidget() {
  const from = todayISO();
  const to = addDaysToISODate(from, 75);
  const raw = useOtherSchedule(OtherScheduleKind.Cleaning, from, to).data ?? [];
  const userMap = userIdMap(useUsers().data ?? []);
  const fsGroups = useFSGroups().data ?? [];
  const settings = useCongSettings().data;
  const byDate = new Map<string, OtherSchedule[]>();
  raw.filter((o) => o.date && o.date >= from).forEach((o) => byDate.set(o.date, [...(byDate.get(o.date) ?? []), o]));
  const rows: PreviewRow[] = [...byDate.keys()]
    .sort((a, b) => dateStringCompare(a, b))
    .slice(0, 3)
    .map((d) => {
      const slots = byDate.get(d) ?? [];
      const names = slots
        .map((o) =>
          settings
            ? cleaningAssigneeName(o, userMap, fsGroups, settings)
            : (userMap.get(o.assigneeUserId ?? 0)?.displayName ?? ""),
        )
        .filter(Boolean);
      return { key: d, dateISO: d, primary: "", names: chipsFrom(names) };
    });
  return <SchedulePreview rows={rows} to="/scheduling/cleaning" />;
}

/** Next few sound & stage dates — the assigned team per date. */
export function AVWidget() {
  const from = todayISO();
  const to = addDaysToISODate(from, 56);
  const lgroup = getStoredLangGroupId();
  const cong = selectedCong(lgroup);
  const year = new Date().getFullYear();
  // assignment dates span at most two calendar years; fetch both so mmDate/wmDate see the CO/custom
  // events that can shift a meeting off its usual weekday.
  const thisYearEvents = useEvents(lgroup, year, { enabled: !!lgroup }).data ?? [];
  const nextYearEvents = useEvents(lgroup, year + 1, { enabled: !!lgroup }).data ?? [];
  const events = [...thisYearEvents, ...nextYearEvents];
  const data = useAVAssignments(from, to, lgroup, { enabled: !!lgroup }).data ?? [];
  const userMap = userIdMap(useUsers().data ?? []);
  const byDate = new Map<string, AVAttendantAssignment[]>();
  data
    .filter((a) => !!a.assignee && a.date >= from)
    .forEach((a) => byDate.set(a.date, [...(byDate.get(a.date) ?? []), a]));
  const rows: PreviewRow[] = [...byDate.keys()]
    .sort((a, b) => (a < b ? -1 : 1))
    .slice(0, 3)
    .map((d) => {
      const list = byDate.get(d) ?? [];
      const names = list.map((a) => userMap.get(a.assignee ?? 0)?.displayName).filter((n): n is string => !!n);
      // AV attendant assignments are keyed by the raw week date: Monday for midweek, Sunday for
      // weekend. Route through mmDate/wmDate the same way the AV attendant screen does to get the
      // actual meeting date when it's been moved by a custom weekday or CO visit.
      const dateISO = cong ? (getDayjs(d).isoWeekday() === 1 ? mmDate(d, cong, events) : wmDate(d, cong, events)) : d;
      return { key: d, dateISO, primary: "", names: chipsFrom(names, 3) };
    });
  return <SchedulePreview rows={rows} to="/scheduling/avattendant" />;
}

/** Next few custom assignments — title + assignees. */
export function CustomAssignmentsWidget() {
  const thisMonth = Month.fromDate(new Date());
  const nextMonth = Month.fromDate(new Date());
  nextMonth.addMonth();
  const q1 = useCustomAssignments(thisMonth).data ?? [];
  const q2 = useCustomAssignments(nextMonth).data ?? [];
  const userMap = userIdMap(useUsers().data ?? []);
  const from = todayISO();
  const rows: PreviewRow[] = [...q1, ...q2]
    .filter((ca) => ca.date >= from)
    .sort((a, b) => dateStringCompare(a.date, b.date))
    .slice(0, 3)
    .map((ca) => {
      const names = ca.assignees.map((a) => userMap.get(a.assigneeId)?.displayName).filter((n): n is string => !!n);
      return { key: ca.id, dateISO: ca.date, primary: ca.title, names: chipsFrom(names) };
    });
  return <SchedulePreview rows={rows} to="/scheduling/custom" />;
}

/** Territories due back soonest (overdue ones flagged) — territories have no forward schedule. */
export function TerritoryDueWidget() {
  const shortDate = useShortDate();
  const records = useTerritoryRecords(true).data ?? [];
  const settings = useCongSettings().data;
  const durationDays = settings?.terr_checkout_duration_days || 120;
  const userMap = userIdMap(useUsers().data ?? []);
  const terrMap = useTerritoryMap();
  const nowMs = new Date().getTime();
  const rows: PreviewRow[] = records
    .filter((r) => r.start !== null && r.end === null)
    .map((r) => ({ rec: r, returnBy: returnByDate(r.start, durationDays) }))
    .sort((a, b) => a.returnBy.valueOf() - b.returnBy.valueOf())
    .slice(0, 3)
    .map(({ rec, returnBy }) => {
      const terr = terrMap.get(rec.territory_id);
      return {
        key: rec.id,
        dateLabel: shortDate(returnBy.format("YYYY-MM-DD")),
        primary: terr ? terrLabel(terr) : `#${rec.territory_id}`,
        secondary: userMap.get(rec.user_id)?.displayName ?? "",
        danger: returnBy.valueOf() < nowMs,
      };
    });
  return <SchedulePreview rows={rows} to="/congregation/territory" />;
}
